import { useState, useRef } from 'react';
import {
    TextField, InputAdornment, IconButton, Paper, Typography
} from '@mui/material';
import { Collections, Send, Close } from '@mui/icons-material';
import { sendMessage } from '../../services/Chat';
import Unknowndm from './Unknowndm';

const MAX_FILE_SIZE_MB = 5; // 5MB

const MessageInput = ({ chatId, showSnackbar, receipientId, isBlocked, isConnected, showMsg, setMessages, setShowMsg, setIsConnected, setIsBlocked }) => {
    const [messageInput, setMessageInput] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const inputRef = useRef(null);
    const [isSending, setIsSending] = useState(false);

    const fileInputRef = useRef(null);

    // 🔹 Open file picker
    const handleImageClick = () => {
        fileInputRef.current.click();
    };

    // 🔹 Select image → show preview (DO NOT SEND YET)
    const handleImageSelect = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 🔹 Optional: ensure only images
        if (!file.type.startsWith("image/")) {
            showSnackbar("Only image files are allowed", "error");
            return;
        }

        // 🔹 Size validation
        const fileSizeMB = file.size / (1024 * 1024);
        if (fileSizeMB > MAX_FILE_SIZE_MB) {
            showSnackbar(
                `File size should be less than ${MAX_FILE_SIZE_MB}MB`,
                "error"
            );
            return;
        }

        // 🔹 Revoke old preview (avoid memory leak)
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }

        // 🔹 Set new preview
        const preview = URL.createObjectURL(file);
        setSelectedImage(file);
        setPreviewUrl(preview);

        // 🔹 Reset input so same image can be selected again
        e.target.value = "";
    };

    // 🔹 Cancel selected image
    const handleCancelImage = () => {
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
        }
        setSelectedImage(null);
        setPreviewUrl(null);
    };


    // 🔹 Send text OR image
    const handleSendMessage = () => {
        // If image selected → send image
        if (selectedImage) {
            const formData = new FormData();
            formData.append("attachments[]", selectedImage);
            formData.append("content", messageInput || "");


            setIsSending(true);
            sendMessage(chatId, formData)
                .then((res) => {
                    console.log("Image sent:", res);
                    setSelectedImage(null);
                    setPreviewUrl(null);
                    setMessageInput("");
                    // 2. Force focus back to input (Double safety)
                    if (inputRef.current) {
                        inputRef.current.focus();
                    }
                })
                .catch((error) => {
                    showSnackbar(error.message || "Failed to send image", "error");
                })
                .finally(() => {
                    setIsSending(false);
                });

            return;
        }

        // Normal text message
        if (!messageInput.trim()) return;

        const data = { content: messageInput };
        setIsSending(true);
        sendMessage(chatId, data)
            .then((res) => {
                console.log("Message sent:", res);
                setMessageInput("");
                if (inputRef.current) {
                    inputRef.current.focus();
                }
            })
            .catch((error) => {
                showSnackbar(error.message || "Failed to send message", "error");
            })
            .finally(() => {
                setIsSending(false);
            });
    };

    // Enter to send
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isSending) {
            handleSendMessage();
            }
        }
    };

    return (
        <>

            {showMsg && <Unknowndm receipientId={receipientId} showSnackbar={showSnackbar} setMessages={setMessages} isBlocked={isBlocked} isConnected={isConnected} setShowMsg={setShowMsg} setIsConnected={setIsConnected} setIsBlocked={setIsBlocked} />}

            {!showMsg && !isConnected && (
                <Typography variant="body2" color="textSecondary" align="center">
                    You are not connected to this user
                </Typography>
            )}

            {!showMsg && isConnected && isBlocked && (
                <Typography variant="body2" color="textSecondary" align="center">
                    You are blocked by this user
                </Typography>
            )}
            
            {!showMsg && isConnected && !isBlocked && (
                <>
                    {/* 🔹 IMAGE PREVIEW BAR */}
                    {previewUrl && (
                        <Paper
                            elevation={3}
                            sx={{
                                borderRadius: 2,
                                width: "fit-content",
                                position: "relative",
                                display: "flex",
                                mb: 1
                            }}
                        >
                            <img
                                src={previewUrl}
                                alt="preview"
                                style={{
                                    width: 80,
                                    height: 80,
                                    objectFit: "cover",
                                    borderRadius: 8
                                }}
                            />

                            <IconButton
                                color="error" onClick={handleCancelImage}
                                sx={{
                                    position: "absolute",
                                    top: 5,
                                    right: 5
                                }}
                            >
                                <Close />
                            </IconButton>
                        </Paper>
                    )}

                    {/* Hidden file input */}
                    <input
                        type="file"
                        accept="image/*"
                        ref={fileInputRef}
                        style={{ display: "none" }}
                        onChange={handleImageSelect}
                    />

                    <TextField
                        inputRef={inputRef}
                        fullWidth
                        placeholder="Type a message..."
                        value={messageInput}
                        onChange={(e) => setMessageInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        multiline
                        maxRows={4}
                        size="small"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconButton color="primary" onClick={handleImageClick}>
                                            <Collections />
                                        </IconButton>
                                    </InputAdornment>
                                ),

                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton color="primary" onClick={handleSendMessage}
                                            onMouseDown={(e) => {
                                                e.preventDefault();
                                            }}
                                            disabled={isSending}
                                        >
                                            <Send />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                                sx: {
                                    borderRadius: 5,
                                    border: 'none',
                                    '& fieldset': { border: 'none' }
                                }
                            }
                        }}
                    />
                </>
            )}
        </>
    );
};

export default MessageInput;
